import React, { useState, useRef } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'line-awesome/dist/line-awesome/css/line-awesome.min.css';
import '../../styles/ResumeBuilder.css';

const ResumeBuilder = () => {
  // State for personal information
  const [personalInfo, setPersonalInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    linkedin: ''
  });

  // State for summary
  const [summary, setSummary] = useState('');

  // State for work experience sections
  const [experiences, setExperiences] = useState([
    { id: 1, jobTitle: '', company: '', location: '', startDate: '', endDate: '', description: '', current: false, bullets: [''] }
  ]);

  // State for education sections
  const [educations, setEducations] = useState([
    { id: 1, degree: '', school: '', location: '', startDate: '', endDate: '', description: '', current: false, bullets: [''] }
  ]);

  // State for skills
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  
  // State for other sections
  const [otherSections, setOtherSections] = useState({
    volunteering: '',
    projects: '',
    awards: ''
  });

  // State for PDF generation
  const [isGenerating, setIsGenerating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const resumeRef = useRef(null);
  
  // Handle changes to personal information
  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo({
      ...personalInfo,
      [name]: value
    });
  };

  // Handle summary changes
  const handleSummaryChange = (e) => {
    setSummary(e.target.value);
  };
  
  // Handle changes to work experience
  const handleExperienceChange = (id, field, value) => {
    setExperiences(experiences.map(exp => {
      if (exp.id === id) {
        const updatedExp = { ...exp, [field]: value };
        
        // Handle the "current" checkbox special case
        if (field === 'current') {
          updatedExp.endDate = value ? 'Present' : '';
        }
        
        return updatedExp;
      }
      return exp;
    }));
  };
  
  // Handle changes to experience bullets
  const handleExperienceBulletChange = (expId, bulletIndex, value) => {
    setExperiences(experiences.map(exp => {
      if (exp.id === expId) {
        const updatedBullets = [...exp.bullets];
        updatedBullets[bulletIndex] = value;
        return { ...exp, bullets: updatedBullets };
      }
      return exp;
    }));
  };
  
  // Add new bullet point to experience
  const addExperienceBullet = (expId) => {
    setExperiences(experiences.map(exp => {
      if (exp.id === expId) {
        return { ...exp, bullets: [...exp.bullets, ''] };
      }
      return exp;
    }));
  };
  
  // Remove bullet point from experience
  const removeExperienceBullet = (expId, bulletIndex) => {
    setExperiences(experiences.map(exp => {
      if (exp.id === expId && exp.bullets.length > 1) {
        const updatedBullets = [...exp.bullets];
        updatedBullets.splice(bulletIndex, 1);
        return { ...exp, bullets: updatedBullets };
      }
      return exp;
    }));
  };
  
  // Add new work experience section
  const addExperience = () => {
    const newId = experiences.length > 0 ? Math.max(...experiences.map(exp => exp.id)) + 1 : 1;
    setExperiences([
      ...experiences,
      { id: newId, jobTitle: '', company: '', location: '', startDate: '', endDate: '', description: '', current: false, bullets: [''] }
    ]);
  };
  
  // Remove work experience section
  const removeExperience = (id) => {
    if (experiences.length > 1) {
      setExperiences(experiences.filter(exp => exp.id !== id));
    }
  };
  
  // Handle changes to education
  const handleEducationChange = (id, field, value) => {
    setEducations(educations.map(edu => {
      if (edu.id === id) {
        const updatedEdu = { ...edu, [field]: value };
        
        // Handle the "current" checkbox special case
        if (field === 'current') {
          updatedEdu.endDate = value ? 'Present' : '';
        }
        
        return updatedEdu;
      }
      return edu;
    }));
  };
  
  // Handle changes to education bullets
  const handleEducationBulletChange = (eduId, bulletIndex, value) => {
    setEducations(educations.map(edu => {
      if (edu.id === eduId) {
        const updatedBullets = [...edu.bullets];
        updatedBullets[bulletIndex] = value;
        return { ...edu, bullets: updatedBullets };
      }
      return edu;
    }));
  };
  
  // Add new bullet point to education
  const addEducationBullet = (eduId) => {
    setEducations(educations.map(edu => {
      if (edu.id === eduId) {
        return { ...edu, bullets: [...edu.bullets, ''] };
      }
      return edu;
    }));
  };
  
  // Remove bullet point from education
  const removeEducationBullet = (eduId, bulletIndex) => {
    setEducations(educations.map(edu => {
      if (edu.id === eduId && edu.bullets.length > 1) {
        const updatedBullets = [...edu.bullets];
        updatedBullets.splice(bulletIndex, 1);
        return { ...edu, bullets: updatedBullets };
      }
      return edu;
    }));
  };
  
  // Add new education section
  const addEducation = () => {
    const newId = educations.length > 0 ? Math.max(...educations.map(edu => edu.id)) + 1 : 1;
    setEducations([
      ...educations,
      { id: newId, degree: '', school: '', location: '', startDate: '', endDate: '', description: '', current: false, bullets: [''] }
    ]);
  };
  
  // Remove education section
  const removeEducation = (id) => {
    if (educations.length > 1) {
      setEducations(educations.filter(edu => edu.id !== id));
    }
  };
  
  // Handle other sections change
  const handleOtherSectionChange = (e) => {
    const { name, value } = e.target;
    setOtherSections({
      ...otherSections,
      [name]: value
    });
  };
  
  // Handle skill input
  const handleSkillInputChange = (e) => {
    setSkillInput(e.target.value);
  };
  
  // Add new skill
  const addSkill = (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      e.preventDefault();
      const trimmedInput = skillInput.trim();
      if (trimmedInput && !skills.includes(trimmedInput)) {
        setSkills([...skills, trimmedInput]);
        setSkillInput('');
      }
    }
  };
  
  // Remove a skill
  const removeSkill = (skillToRemove) => {
    setSkills(skills.filter(skill => skill !== skillToRemove));
  };
  
  // Generate and download PDF
  const generatePDF = async () => {
    if (!personalInfo.fullName) {
      alert('Please enter at least your name to generate a resume');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      // Get the resume element
      const resumeElement = resumeRef.current;
      
      // Temporarily adjust the element to ensure full capture
      const originalStyles = {
        maxHeight: resumeElement.style.maxHeight,
        overflow: resumeElement.style.overflow,
      };
      
      resumeElement.style.maxHeight = 'none';
      resumeElement.style.overflow = 'visible';
      
      // Generate canvas
      const canvas = await html2canvas(resumeElement, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
        windowHeight: 1600,
        scrollX: 0,
        scrollY: -window.scrollY,
        // Capture the full height
        height: resumeElement.scrollHeight,
        // Use a slight delay to ensure the document is fully rendered
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById(resumeElement.id);
          if (clonedElement) {
            clonedElement.style.maxHeight = 'none';
            clonedElement.style.overflow = 'visible';
          }
          return new Promise(resolve => setTimeout(resolve, 500));
        }
      });
      
      // Restore original styles
      resumeElement.style.maxHeight = originalStyles.maxHeight;
      resumeElement.style.overflow = originalStyles.overflow;
      
      // Get canvas data
      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF with correct dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Create PDF document
      const pdf = new jsPDF({
        format: 'a4',
        unit: 'mm',
        orientation: imgHeight > pageHeight ? 'portrait' : 'portrait',
      });
      
      // Handle multi-page case for longer resumes
      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        position = -pageHeight * pageNumber;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        pageNumber++;
      }
      
      // Save the PDF
      const fileName = `${personalInfo.fullName.replace(/\s/g, '_')}_Resume.pdf`;
      pdf.save(fileName);
      
      setSuccessMessage('Resume downloaded successfully!');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Container className="page-container resume-builder-container">
      <Helmet>
        <title>Resume Builder - Create ATS-Friendly Resume | MicroGenie</title>
        <meta name="description" content="Create a professional, ATS-compliant resume with our free Resume Builder tool. Add your work experience, education, and skills to generate a standout resume." />
        <meta name="keywords" content="resume builder, free resume builder, ATS resume creator, professional CV maker, job application resume" />
      </Helmet>

      <div className="text-center mb-5">
        <h1 className="section-title">Resume Builder</h1>
        <p className="mx-auto" style={{ maxWidth: '700px' }}>
          Create a professional, ATS-compliant resume in minutes. Fill in your details below and download a perfectly formatted PDF resume.
        </p>
      </div>
      
      {successMessage && (
        <Alert variant="success" className="text-center mb-4">
          {successMessage}
        </Alert>
      )}
      
      <div className="guide-card mb-4">
        <h4 className="guide-title">Resume Writing Tips</h4>
        <ul className="guide-list">
          <li>Use specific keywords from the job description to pass ATS scans</li>
          <li>Quantify your achievements with numbers when possible (e.g., "Increased sales by 25%")</li>
          <li>Keep your resume concise and focused on relevant experience</li>
          <li>Proofread carefully to avoid typos and grammatical errors</li>
        </ul>
      </div>

      <Row>
        {/* Form Column */}
        <Col lg={7}>
          {/* Personal Information Section */}
          <div className="form-section">
            <h2 className="form-section-title">
              <i className="la la-user" aria-hidden="true"></i> Personal Information
            </h2>
            <Form>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Full Name*</Form.Label>
                    <Form.Control
                      type="text"
                      name="fullName"
                      value={personalInfo.fullName}
                      onChange={handlePersonalInfoChange}
                      placeholder="John Smith"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email*</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={personalInfo.email}
                      onChange={handlePersonalInfoChange}
                      placeholder="johnsmith@example.com"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={personalInfo.phone}
                      onChange={handlePersonalInfoChange}
                      placeholder="(123) 456-7890"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={personalInfo.address}
                      onChange={handlePersonalInfoChange}
                      placeholder="City, State"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>LinkedIn</Form.Label>
                    <Form.Control
                      type="text"
                      name="linkedin"
                      value={personalInfo.linkedin}
                      onChange={handlePersonalInfoChange}
                      placeholder="linkedin.com/in/johnsmith"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>

          {/* Summary Section */}
          <div className="form-section">
            <h2 className="form-section-title">
              <i className="la la-align-left" aria-hidden="true"></i> Professional Summary
            </h2>
            <Form.Group className="mb-3">
              <Form.Label>Summary</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={summary}
                onChange={handleSummaryChange}
                placeholder="Brief summary of your professional background, key skills, and career objectives..."
              />
              <Form.Text className="text-muted">
                A concise 2-3 sentence summary highlighting your expertise, years of experience, and key accomplishments.
              </Form.Text>
            </Form.Group>
          </div>

          {/* Work Experience Section */}
          <div className="form-section">
            <h2 className="form-section-title">
              <i className="la la-briefcase" aria-hidden="true"></i> Professional Experience
            </h2>
            
            {experiences.map((experience) => (
              <div key={experience.id} className="section-card">
                <div className="section-card-header">
                  <h5>Experience #{experiences.indexOf(experience) + 1}</h5>
                  <div className="section-actions">
                    <button 
                      type="button" 
                      className="action-btn delete-btn"
                      onClick={() => removeExperience(experience.id)}
                    >
                      <i className="la la-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Job Title</Form.Label>
                      <Form.Control
                        type="text"
                        value={experience.jobTitle}
                        onChange={(e) => handleExperienceChange(experience.id, 'jobTitle', e.target.value)}
                        placeholder="Data Engineer"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company</Form.Label>
                      <Form.Control
                        type="text"
                        value={experience.company}
                        onChange={(e) => handleExperienceChange(experience.id, 'company', e.target.value)}
                        placeholder="Tech Company Inc."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Location</Form.Label>
                      <Form.Control
                        type="text"
                        value={experience.location}
                        onChange={(e) => handleExperienceChange(experience.id, 'location', e.target.value)}
                        placeholder="San Francisco, CA"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Row>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Date</Form.Label>
                          <Form.Control
                            type="text"
                            value={experience.startDate}
                            onChange={(e) => handleExperienceChange(experience.id, 'startDate', e.target.value)}
                            placeholder="Jun 2020"
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label>End Date</Form.Label>
                          <Form.Control
                            type="text"
                            value={experience.endDate}
                            onChange={(e) => handleExperienceChange(experience.id, 'endDate', e.target.value)}
                            placeholder="Present"
                            disabled={experience.current}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id={`current-job-${experience.id}`}
                        label="I currently work here"
                        checked={experience.current}
                        onChange={(e) => handleExperienceChange(experience.id, 'current', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  
                  {/* Bullet points for experience */}
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Key Achievements/Responsibilities (Bullet Points)</Form.Label>
                      {experience.bullets.map((bullet, index) => (
                        <div key={index} className="bullet-input-group mb-2">
                          <div className="d-flex align-items-center">
                            <div className="bullet-point-icon">•</div>
                            <Form.Control
                              type="text"
                              value={bullet}
                              onChange={(e) => handleExperienceBulletChange(experience.id, index, e.target.value)}
                              placeholder="Describe an achievement or responsibility (preferably with metrics)"
                            />
                            <div className="bullet-actions">
                              {experience.bullets.length > 1 && (
                                <button
                                  type="button"
                                  className="action-btn remove-bullet"
                                  onClick={() => removeExperienceBullet(experience.id, index)}
                                >
                                  <i className="la la-times" aria-hidden="true"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2 add-bullet-btn"
                        onClick={() => addExperienceBullet(experience.id)}
                      >
                        <i className="la la-plus" aria-hidden="true"></i> Add Bullet Point
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}
            
            <button 
              type="button" 
              className="add-section-btn"
              onClick={addExperience}
            >
              <i className="la la-plus" aria-hidden="true"></i> Add Another Experience
            </button>
          </div>

          {/* Education Section */}
          <div className="form-section">
            <h2 className="form-section-title">
              <i className="la la-graduation-cap" aria-hidden="true"></i> Education
            </h2>
            
            {educations.map((education) => (
              <div key={education.id} className="section-card">
                <div className="section-card-header">
                  <h5>Education #{educations.indexOf(education) + 1}</h5>
                  <div className="section-actions">
                    <button 
                      type="button" 
                      className="action-btn delete-btn"
                      onClick={() => removeEducation(education.id)}
                    >
                      <i className="la la-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Degree / Program</Form.Label>
                      <Form.Control
                        type="text"
                        value={education.degree}
                        onChange={(e) => handleEducationChange(education.id, 'degree', e.target.value)}
                        placeholder="Bachelor of Science in Computer Science"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>School</Form.Label>
                      <Form.Control
                        type="text"
                        value={education.school}
                        onChange={(e) => handleEducationChange(education.id, 'school', e.target.value)}
                        placeholder="University Name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Location</Form.Label>
                      <Form.Control
                        type="text"
                        value={education.location}
                        onChange={(e) => handleEducationChange(education.id, 'location', e.target.value)}
                        placeholder="City, State"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Row>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Date</Form.Label>
                          <Form.Control
                            type="text"
                            value={education.startDate}
                            onChange={(e) => handleEducationChange(education.id, 'startDate', e.target.value)}
                            placeholder="Aug 2016"
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label>End Date</Form.Label>
                          <Form.Control
                            type="text"
                            value={education.endDate}
                            onChange={(e) => handleEducationChange(education.id, 'endDate', e.target.value)}
                            placeholder="May 2020"
                            disabled={education.current}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id={`current-education-${education.id}`}
                        label="I'm currently studying here"
                        checked={education.current}
                        onChange={(e) => handleEducationChange(education.id, 'current', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  
                  {/* Bullet points for education */}
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Achievements/Activities (Bullet Points)</Form.Label>
                      {education.bullets.map((bullet, index) => (
                        <div key={index} className="bullet-input-group mb-2">
                          <div className="d-flex align-items-center">
                            <div className="bullet-point-icon">•</div>
                            <Form.Control
                              type="text"
                              value={bullet}
                              onChange={(e) => handleEducationBulletChange(education.id, index, e.target.value)}
                              placeholder="Add awards, honors, relevant coursework, or activities"
                            />
                            <div className="bullet-actions">
                              {education.bullets.length > 1 && (
                                <button
                                  type="button"
                                  className="action-btn remove-bullet"
                                  onClick={() => removeEducationBullet(education.id, index)}
                                >
                                  <i className="la la-times" aria-hidden="true"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2 add-bullet-btn"
                        onClick={() => addEducationBullet(education.id)}
                      >
                        <i className="la la-plus" aria-hidden="true"></i> Add Bullet Point
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}
            
            <button 
              type="button" 
              className="add-section-btn"
              onClick={addEducation}
            >
              <i className="la la-plus" aria-hidden="true"></i> Add Another Education
            </button>
          </div>

          {/* Skills Section */}
          <div className="form-section">
            <h2 className="form-section-title">
              <i className="la la-tools" aria-hidden="true"></i> Skills & Other
            </h2>
            
            <Form.Group className="mb-4">
              <Form.Label>Technical Skills</Form.Label>
              <div className="skills-input-container">
                {skills.map((skill, index) => (
                  <div key={index} className="skill-badge">
                    {skill}
                    <button 
                      type="button" 
                      className="remove-skill"
                      onClick={() => removeSkill(skill)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <input
                  type="text"
                  className="skill-input"
                  value={skillInput}
                  onChange={handleSkillInputChange}
                  onKeyDown={addSkill}
                  onBlur={addSkill}
                  placeholder="Type skill and press Enter"
                />
              </div>
              <Form.Text className="text-muted">
                Press Enter after each skill to add it to your resume
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Volunteering (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="volunteering"
                value={otherSections.volunteering}
                onChange={handleOtherSectionChange}
                placeholder="Describe any volunteer work (e.g., 'Volunteer 20 hours/month at the ATG foundation, leading pro-bono city projects')"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Projects (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="projects"
                value={otherSections.projects}
                onChange={handleOtherSectionChange}
                placeholder="Describe notable projects (e.g., 'Built forecasting using parameters, trend lines, and reference lines which saved 30 hours/week')"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Awards (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="awards"
                value={otherSections.awards}
                onChange={handleOtherSectionChange}
                placeholder="List any awards or recognitions (e.g., 'Teaching Fellow (only 5 awarded to class), Dean's List 2012 (Top 10%)')"
              />
            </Form.Group>
          </div>
        </Col>
        
        {/* Preview Column */}
        <Col lg={5}>
          <div className="preview-container">
            <div className="preview-title">
              <i className="la la-eye" aria-hidden="true"></i> Resume Preview
            </div>
            
            <div id="resume-preview" className="preview-resume" ref={resumeRef}>
              <div className="resume resume-ats">
                {/* Resume Header */}
                <div className="resume-header-ats">
                  <h1 className="resume-name-ats">
                    {personalInfo.fullName || 'Your Name'}
                  </h1>
                  <div className="resume-contact-ats">
                    {personalInfo.address && <span>{personalInfo.address}</span>}
                    {personalInfo.address && (personalInfo.phone || personalInfo.email || personalInfo.linkedin) && <span className="contact-bullet">•</span>}
                    {personalInfo.phone && <span>{personalInfo.phone}</span>}
                    {personalInfo.phone && (personalInfo.email || personalInfo.linkedin) && <span className="contact-bullet">•</span>}
                    {personalInfo.email && <span>{personalInfo.email}</span>}
                    {personalInfo.email && personalInfo.linkedin && <span className="contact-bullet">•</span>}
                    {personalInfo.linkedin && <span>{personalInfo.linkedin}</span>}
                  </div>
                </div>
                
                {/* Summary */}
                {summary && (
                  <div className="resume-section-ats">
                    <h2 className="resume-section-title-ats">SUMMARY</h2>
                    <div className="resume-section-content-ats">
                      <p>{summary}</p>
                    </div>
                  </div>
                )}
                
                {/* Professional Experience */}
                {experiences.some(exp => exp.jobTitle || exp.company) && (
                  <div className="resume-section-ats">
                    <h2 className="resume-section-title-ats">PROFESSIONAL EXPERIENCE</h2>
                    {experiences
                      .filter(exp => exp.jobTitle || exp.company)
                      .map((exp, index) => (
                        <div key={index} className="experience-item-ats">
                          <div className="item-header-ats">
                            <div className="item-company-ats">{exp.company}{exp.location && `, ${exp.location}`}</div>
                            <div className="item-date-ats">
                              {exp.startDate}{exp.startDate && (exp.endDate && ' – ')}{exp.endDate}
                            </div>
                          </div>
                          <div className="item-title-ats">{exp.jobTitle}</div>
                          <ul className="item-bullets-ats">
                            {exp.bullets.filter(bullet => bullet.trim() !== '').map((bullet, bulletIndex) => (
                              <li key={bulletIndex}>{bullet}</li>
                            ))}
                          </ul>
                        </div>
                      ))
                    }
                  </div>
                )}
                
                {/* Education */}
                {educations.some(edu => edu.degree || edu.school) && (
                  <div className="resume-section-ats">
                    <h2 className="resume-section-title-ats">EDUCATION</h2>
                    {educations
                      .filter(edu => edu.degree || edu.school)
                      .map((edu, index) => (
                        <div key={index} className="education-item-ats">
                          <div className="item-header-ats">
                            <div className="item-school-ats">{edu.school}{edu.location && `, ${edu.location}`}</div>
                            <div className="item-date-ats">
                              {edu.startDate}{edu.startDate && (edu.endDate && ' – ')}{edu.endDate}
                            </div>
                          </div>
                          <div className="item-degree-ats">{edu.degree}</div>
                          {edu.bullets.some(bullet => bullet.trim() !== '') && (
                            <ul className="item-bullets-ats">
                              {edu.bullets.filter(bullet => bullet.trim() !== '').map((bullet, bulletIndex) => (
                                <li key={bulletIndex}>{bullet}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))
                    }
                  </div>
                )}
                
                {/* Skills & Other */}
                {(skills.length > 0 || otherSections.volunteering || otherSections.projects || otherSections.awards) && (
                  <div className="resume-section-ats">
                    <h2 className="resume-section-title-ats">SKILLS & OTHER</h2>
                    
                    {skills.length > 0 && (
                      <div className="other-item-ats">
                        <div className="other-label-ats">Skills:</div>
                        <div className="other-content-ats">{skills.join(', ')}</div>
                      </div>
                    )}
                    
                    {otherSections.volunteering && (
                      <div className="other-item-ats">
                        <div className="other-label-ats">Volunteering:</div>
                        <div className="other-content-ats">{otherSections.volunteering}</div>
                      </div>
                    )}
                    
                    {otherSections.projects && (
                      <div className="other-item-ats">
                        <div className="other-label-ats">Projects:</div>
                        <div className="other-content-ats">{otherSections.projects}</div>
                      </div>
                    )}
                    
                    {otherSections.awards && (
                      <div className="other-item-ats">
                        <div className="other-label-ats">Awards:</div>
                        <div className="other-content-ats">{otherSections.awards}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <Button
              variant="primary"
              className="download-btn"
              onClick={generatePDF}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <i className="la la-spinner la-spin" aria-hidden="true"></i> Generating PDF...
                </>
              ) : (
                <>
                  <i className="la la-download" aria-hidden="true"></i> Download PDF Resume
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ResumeBuilder;